import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogoWithText } from '../components/Logo';
import { ArrowRight, List, X, BookOpen, CaretRight } from 'phosphor-react';
import './LandingPage.css';

const docs = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    items: [
      { title: 'Welcome to Campusio', description: 'An introduction to the platform and what you can do with it.' },
      { title: 'Account Setup', description: 'Step-by-step guide to set up your school and initial configuration.' },
      { title: 'User Roles & Permissions', description: 'Understanding user roles — super admin, school admin, teacher, parent, student, security officer, driver.' },
      { title: 'Creating Accounts', description: 'How admins create and distribute staff, parent, and student accounts with first-login password reset.' },
      { title: 'Your First Steps', description: 'Quick walkthrough to get up and running in 30 minutes.' },
    ],
  },
  {
    id: 'academic',
    title: 'Academic Module',
    items: [
      { title: 'Student Management', description: 'Add, edit, and manage student records and bulk imports via CSV.' },
      { title: 'Attendance Tracking', description: 'Mark and track student attendance with detailed reports.' },
      { title: 'Grade Management', description: 'Enter grades, generate report cards, and track student performance.' },
      { title: 'Assignments & Grading', description: 'Create assignments and grade student work using rubrics.' },
      { title: 'Timetable Management', description: 'Create and manage class schedules.' },
    ],
  },
  {
    id: 'finance',
    title: 'Finance & Accounting',
    items: [
      { title: 'Chart of Accounts', description: 'Set up and manage your general ledger accounts.' },
      { title: 'Journal Entries', description: 'Record financial transactions with double-entry bookkeeping.' },
      { title: 'Financial Reports', description: 'Generate P&L, balance sheets, and trial balances.' },
      { title: 'Expense Management', description: 'Track expenses with approval workflows.' },
      { title: 'Billing & Fees', description: 'Set up fee structures and manage student billing.' },
    ],
  },
  {
    id: 'security',
    title: 'Student Security',
    items: [
      { title: 'Overview', description: 'QR-based gate security and collector tracking system for safe dismissal.' },
      { title: 'QR Token Setup', description: 'How daily QR codes are generated and shared with parents via the parent portal.' },
      { title: 'Gate Scanning', description: 'Using the officer scan interface to verify pickups and mark arrival.' },
      { title: 'Collector Tracking', description: 'How parents share live location for collector sessions.' },
      { title: 'Admin Tracking Dashboard', description: 'Real-time map view for security officers showing all student statuses and bus positions.' },
      { title: 'Driver GPS Tracking', description: 'Driver app: selecting a route and broadcasting live GPS.' },
    ],
  },
  {
    id: 'transport',
    title: 'Transport Module',
    items: [
      { title: 'Vehicle Management', description: "Manage your school's vehicle fleet and maintenance." },
      { title: 'Route Management', description: 'Create and optimise transport routes.' },
      { title: 'Driver Management', description: 'Manage driver information and vehicle assignments.' },
      { title: 'Student Enrolment', description: 'Enrol students in transport services.' },
      { title: 'Attendance Tracking', description: 'Track daily transport attendance.' },
    ],
  },
  {
    id: 'hostel',
    title: 'Hostel Module',
    items: [
      { title: 'Hostel Setup', description: 'Configure hostels and room allocations.' },
      { title: 'Room Allocation', description: 'Assign students to rooms and manage capacity.' },
      { title: 'Attendance & Check-in', description: 'Track resident attendance and check-in/out.' },
      { title: 'Visitor Management', description: 'Log and manage visitor visits.' },
      { title: 'Complaints & Maintenance', description: 'Handle complaints and maintenance requests.' },
    ],
  },
  {
    id: 'integration',
    title: 'Integration & APIs',
    items: [
      { title: 'Payment Integration', description: 'Set up Paystack integration for online payments.' },
      { title: 'SMS Integration', description: 'Configure SMS service for notifications and alerts.' },
      { title: 'CSV Import/Export', description: 'Bulk import students, staff, and users via CSV.' },
      { title: 'REST API', description: 'Access our REST API documentation for custom integrations.' },
    ],
  },
];

export default function DocumentationPage() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('getting-started');

  const activeSection = docs.find(d => d.id === activeTab);
  const navLinks = [['/', 'Home'], ['/about', 'About'], ['/features', 'Features'], ['/contact', 'Contact']];

  return (
    <div style={{ background: 'var(--land-white)', color: 'var(--land-text)', fontFamily: "'IBM Plex Sans', sans-serif" }}>

      {/* Nav */}
      <nav className="lp-nav" style={{ background: 'rgba(255,255,255,0.97)', borderBottom: '1px solid var(--land-border)' }}>
        <div className="lp-nav-inner">
          <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <LogoWithText size={30} />
          </button>
          <div className="lp-nav-links">
            {navLinks.map(([path, label]) => (
              <button key={path} className="lp-nav-link" onClick={() => navigate(path)}>{label}</button>
            ))}
          </div>
          <div className="lp-nav-actions">
            <button className="lp-btn-ghost" onClick={() => navigate('/login')}>Sign In</button>
            <button className="lp-btn-primary" onClick={() => navigate('/contact')}>
              Request a Demo <ArrowRight size={15} weight="bold" />
            </button>
          </div>
          <button onClick={() => setMenuOpen(true)} className="lp-hamburger" style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <List size={26} color="var(--land-text)" />
          </button>
        </div>
      </nav>

      {menuOpen && (
        <div className="lp-mobile-menu">
          <button className="lp-mobile-close" onClick={() => setMenuOpen(false)}><X size={20} /></button>
          {navLinks.map(([path, label]) => (
            <button key={path} className="lp-mobile-link" onClick={() => { navigate(path); setMenuOpen(false); }}>{label}</button>
          ))}
          <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 24, borderTop: '1px solid var(--land-border)' }}>
            <button className="lp-btn-outline-lg" style={{ justifyContent: 'center' }} onClick={() => { navigate('/login'); setMenuOpen(false); }}>Sign In</button>
            <button className="lp-btn-primary-lg" style={{ justifyContent: 'center' }} onClick={() => { navigate('/contact'); setMenuOpen(false); }}>Request a Demo</button>
          </div>
        </div>
      )}

      {/* Hero */}
      <section style={{ paddingTop: 120, paddingBottom: 64, textAlign: 'center', background: 'var(--land-off)', borderBottom: '1px solid var(--land-border)' }}>
        <div style={{ maxWidth: 640, margin: '0 auto', padding: '0 24px' }}>
          <p className="lp-section-label" style={{ justifyContent: 'center', display: 'flex' }}>
            <BookOpen size={14} style={{ marginRight: 6 }} /> Documentation
          </p>
          <h1 style={{ fontSize: 'clamp(32px,5vw,48px)', fontWeight: 800, color: 'var(--land-text)', letterSpacing: '-0.03em', fontFamily: 'Manrope, sans-serif', marginBottom: 16, lineHeight: 1.1 }}>
            Learn how to use Campusio
          </h1>
          <p style={{ fontSize: 17, color: 'var(--land-muted)', lineHeight: 1.65 }}>
            Guides and reference docs for administrators, teachers, parents, and technical staff.
          </p>
        </div>
      </section>

      {/* Docs layout */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '64px 24px', display: 'grid', gridTemplateColumns: '220px 1fr', gap: 48, alignItems: 'start' }}>

        {/* Sidebar */}
        <nav style={{ position: 'sticky', top: 80 }}>
          {docs.map(({ id, title }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                width: '100%', textAlign: 'left', padding: '9px 12px',
                borderRadius: 8, border: 'none', cursor: 'pointer',
                fontWeight: activeTab === id ? 700 : 500,
                fontSize: 14,
                background: activeTab === id ? 'var(--land-stone)' : 'none',
                color: activeTab === id ? 'var(--land-forest)' : 'var(--land-muted)',
                marginBottom: 2,
                transition: 'background 0.1s',
              }}
            >
              {activeTab === id && <CaretRight size={12} weight="bold" />}
              {title}
            </button>
          ))}
        </nav>

        {/* Content */}
        <div>
          <h2 style={{ fontSize: 26, fontWeight: 800, color: 'var(--land-text)', fontFamily: 'Manrope, sans-serif', marginBottom: 8, letterSpacing: '-0.02em' }}>
            {activeSection?.title}
          </h2>
          <div style={{ height: 2, width: 48, background: 'var(--land-forest)', borderRadius: 2, marginBottom: 32 }} />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {activeSection?.items.map(({ title, description }, i) => (
              <div
                key={title}
                style={{
                  padding: '20px 0',
                  borderBottom: i < activeSection.items.length - 1 ? '1px solid var(--land-border)' : 'none',
                  display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16,
                  cursor: 'default'
                }}
              >
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--land-text)', marginBottom: 4 }}>{title}</div>
                  <div style={{ fontSize: 14, color: 'var(--land-muted)', lineHeight: 1.6 }}>{description}</div>
                </div>
                <div style={{ flexShrink: 0, fontSize: 11, fontWeight: 700, color: '#166534', background: '#ecfdf5', padding: '3px 10px', borderRadius: 100, border: '1px solid #bbf7d0', whiteSpace: 'nowrap' }}>
                  Available
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 48, padding: 28, background: 'var(--land-stone)', borderRadius: 12, border: '1px solid var(--land-border)' }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--land-text)', marginBottom: 8 }}>Need help?</div>
            <p style={{ fontSize: 14, color: 'var(--land-muted)', lineHeight: 1.6, marginBottom: 16 }}>
              Our support team is available Mon–Fri, 9AM–5PM GMT. Reach us by phone, WhatsApp, or the contact form.
            </p>
            <button className="lp-btn-ghost" onClick={() => navigate('/contact')} style={{ fontSize: 14 }}>
              Contact support <ArrowRight size={13} style={{ marginLeft: 4, verticalAlign: 'middle' }} />
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ borderTop: '1px solid var(--land-border)', padding: '24px 32px', textAlign: 'center', background: 'var(--land-off)' }}>
        <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--land-muted)' }}>
          &larr; Back to Campusio
        </button>
      </div>
    </div>
  );
}
